import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

import { ApiError } from 'src/api/client';
import { signIn } from 'src/api/auth';
import { BrandLogo } from 'src/components/ui/BrandLogo';
import { Button } from 'src/components/ui/Button';
import { Notice } from 'src/components/ui/Notice';
import { ThemeToggle } from 'src/components/ui/ThemeToggle';
import { readEditor, readToken, startSession, subscribeToSession, type SessionEditor } from 'src/lib/session';

/**
 * Nothing in this dashboard is public.
 *
 * The API used to be reachable by anyone who found its URL, which is why deleting a voice lived in
 * a shell command and why queueing a job — an EC2 boot plus paid model calls — was bounded by a
 * daily cap rather than by who was asking. The gate is the other half of closing that: the API
 * refuses an unauthenticated request, and this stops the editor from meeting the refusal as
 * fourteen simultaneous 401s.
 *
 * It wraps the router rather than sitting on a `/login` route on purpose. A signed-out editor
 * following a deep link to a broadcast should land on that broadcast once they sign in, and a
 * redirect to `/login` and back is a way to lose the link.
 */
export function SignInGate({ children }: { children: React.ReactNode }) {
  const token = useSyncExternalStore(subscribeToSession, readToken, () => null);

  if (token) return <>{children}</>;
  return <SignInScreen />;
}

/** The signed-in editor, for screens that want to name them. `null` while signed out. */
export function useSessionEditor(): SessionEditor | null {
  return useSyncExternalStore(subscribeToSession, readEditor, () => null);
}

function SignInScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError(null);
      try {
        const { token, editor } = await signIn(email.trim(), password);
        startSession(token, editor);
      } catch (cause: unknown) {
        setError(describeSignInFailure(cause));
        setBusy(false);
      }
    },
    [email, password]
  );

  // Nothing else is on screen, so the first field should already be the one being typed into.
  useEffect(() => {
    document.getElementById('signin-email')?.focus();
  }, []);

  return (
    <div className="signin">
      <div className="signin__utility">
        <ThemeToggle />
      </div>
      <form className="signin__card" onSubmit={submit}>
        {/* The one screen with nothing else on it, so it gets the full lockup rather than the
            eyebrow that used to stand in for it. */}
        <BrandLogo variant="horizontal" className="signin__logo" />
        <h1 className="signin__title">Escritorio editorial</h1>
        <p className="signin__lede">Entra con tu cuenta para revisar transmisiones, notas y voces.</p>

        <label className="signin__field" htmlFor="signin-email">
          <span>Correo</span>
          <input
            id="signin-email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={event => setEmail(event.target.value)}
          />
        </label>

        <label className="signin__field" htmlFor="signin-password">
          <span>Contraseña</span>
          <input
            id="signin-password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={event => setPassword(event.target.value)}
          />
        </label>

        {error && (
          <Notice tone="contradicted" title="No se pudo entrar" role="alert">
            <p>{error}</p>
          </Notice>
        )}

        <Button type="submit" variant="primary" busy={busy}>
          Entrar
        </Button>
      </form>
    </div>
  );
}

/**
 * Three failures an editor can act on, told apart. "HTTP 403" is the one that matters most: the
 * password was right and the account exists, so retyping it forever will not help — someone has to
 * grant them editorial access.
 */
function describeSignInFailure(cause: unknown): string {
  if (!(cause instanceof ApiError)) return 'Error desconocido al iniciar sesión.';
  if (cause.status === 401) return 'Correo o contraseña incorrectos.';
  if (cause.status === 403) return 'Tu cuenta ya no tiene acceso. Pídeselo a un administrador.';
  if (cause.status === 0) return 'No se pudo contactar al servidor.';
  return cause.message;
}
